import {
  CircleDot,
  GitFork,
  Maximize2,
  Minimize2,
  Minus,
  Plus,
  RotateCcw,
  Scan,
} from 'lucide-react';
import { useCanvasStore } from '../../store/useCanvasStore';
import { useSimulationStore } from '../../store/useSimulationStore';
import { calculateRadialLayout } from './RadialLayout';
import { calculateTreeLayout } from './TreeLayout';

export const CanvasControls: React.FC = () => {
  const layoutMode = useCanvasStore((state) => state.layoutMode);
  const setLayoutMode = useCanvasStore((state) => state.setLayoutMode);
  const treeOrientation = useCanvasStore((state) => state.treeOrientation);
  const setTreeOrientation = useCanvasStore((state) => state.setTreeOrientation);
  const zoomIn = useCanvasStore((state) => state.zoomIn);
  const zoomOut = useCanvasStore((state) => state.zoomOut);
  const resetCamera = useCanvasStore((state) => state.resetCamera);
  const isFullscreen = useCanvasStore((state) => state.isFullscreen);
  const setIsFullscreen = useCanvasStore((state) => state.setIsFullscreen);
  const fitToScreen = useCanvasStore((state) => state.fitToScreen);

  const getCurrentRootNode = useSimulationStore((state) => state.getCurrentRootNode);

  const handleFitToScreen = () => {
    const root = getCurrentRootNode();
    const layout =
      layoutMode === 'radial'
        ? calculateRadialLayout(root, false)
        : calculateTreeLayout(root, treeOrientation, false);

    const visibleCanvas = Array.from(
      document.querySelectorAll<HTMLElement>('[data-canvas-container="true"]')
    ).find((el) => el.clientWidth > 0 && el.clientHeight > 0);
    const width = visibleCanvas?.clientWidth;
    const height = visibleCanvas?.clientHeight;

    fitToScreen(width, height, layout.bounds);
  };

  const handleResetCamera = () => {
    const root = getCurrentRootNode();
    const layout =
      layoutMode === 'radial'
        ? calculateRadialLayout(root, false)
        : calculateTreeLayout(root, treeOrientation, false);

    const visibleCanvas = Array.from(
      document.querySelectorAll<HTMLElement>('[data-canvas-container="true"]')
    ).find((el) => el.clientWidth > 0 && el.clientHeight > 0);
    const width = visibleCanvas?.clientWidth;
    const height = visibleCanvas?.clientHeight;

    resetCamera(width, height, layout.bounds);
  };

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.().catch(() => {});
      setIsFullscreen(false);
    }
  };

  return (
    <div className="absolute bottom-20 md:bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 p-1.5 rounded-2xl bg-[#0F172A]/90 backdrop-blur-xl border border-slate-700/80 shadow-[0_10px_30px_rgba(0,0,0,0.6)] text-slate-300 select-none">
      {/* Zoom In */}
      <button
        type="button"
        onClick={() => zoomIn()}
        title="Збільшити масштаб (+)"
        className="p-2 rounded-xl hover:bg-slate-700/60 hover:text-emerald-400 active:scale-95 transition-all text-slate-300"
      >
        <Plus className="w-4 h-4" />
      </button>

      {/* Zoom Out */}
      <button
        type="button"
        onClick={() => zoomOut()}
        title="Зменшити масштаб (-)"
        className="p-2 rounded-xl hover:bg-slate-700/60 hover:text-emerald-400 active:scale-95 transition-all text-slate-300"
      >
        <Minus className="w-4 h-4" />
      </button>

      {/* Center & Fit */}
      <button
        type="button"
        onClick={handleFitToScreen}
        title="Центрувати всю структуру"
        className="p-2 rounded-xl hover:bg-slate-700/60 hover:text-cyan-400 active:scale-95 transition-all text-slate-300"
      >
        <Scan className="w-4 h-4" />
      </button>

      {/* Reset */}
      <button
        type="button"
        onClick={handleResetCamera}
        title="Скинути масштаб (1:1)"
        className="p-2 rounded-xl hover:bg-slate-700/60 hover:text-amber-400 active:scale-95 transition-all text-slate-300"
      >
        <RotateCcw className="w-4 h-4" />
      </button>

      <div className="w-px h-5 bg-slate-700/70 mx-1" />

      {/* Radial / Tree Mode Toggle */}
      <button
        type="button"
        onClick={() => setLayoutMode(layoutMode === 'radial' ? 'tree' : 'radial')}
        title={layoutMode === 'radial' ? 'Перемкнути на ієрархічне дерево' : 'Перемкнути на кругову полярну мапу'}
        className={`p-2 rounded-xl active:scale-95 transition-all ${
          layoutMode === 'radial'
            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
            : 'hover:bg-slate-700/60 text-slate-300'
        }`}
      >
        {layoutMode === 'radial' ? <CircleDot className="w-4 h-4" /> : <GitFork className="w-4 h-4" />}
      </button>

      {/* Tree Orientation Toggle (only in tree mode) */}
      {layoutMode === 'tree' && (
        <button
          type="button"
          onClick={() => setTreeOrientation(treeOrientation === 'vertical' ? 'horizontal' : 'vertical')}
          title={treeOrientation === 'vertical' ? 'Горизонтальне дерево' : 'Вертикальне дерево'}
          className="px-2 py-1 text-xs font-semibold rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:text-white"
        >
          {treeOrientation === 'vertical' ? 'Верт' : 'Гориз'}
        </button>
      )}

      <div className="w-px h-5 bg-slate-700/70 mx-1" />

      {/* Fullscreen Toggle */}
      <button
        type="button"
        onClick={handleToggleFullscreen}
        title={isFullscreen ? 'Вийти з повного екрану' : 'На повний екран'}
        className="p-2 rounded-xl hover:bg-slate-700/60 hover:text-white active:scale-95 transition-all text-slate-300"
      >
        {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
      </button>
    </div>
  );
};
